import React, { useEffect, useRef } from 'react';
import { isEmpty } from 'lodash';
import { Gql } from '@fiverr-private/dynamic_matching';
import { Text, Typography } from '@fiverr-private/typography';
import { ChoiceGroup, ChoiceGroupItem } from '@fiverr-private/forms';
import { Stack, Wrap } from '@fiverr-private/layout_components';
import { HourlyL1Icon, SinglePaymentL1Icon } from '@fiverr-private/visuals';
import { atoms } from '@fiverr-private/theme';
import { DmBriefNeedType } from '@fiverr-private/dynamic_matching/dist/esm/src/gql-operations/gql-generated';
import { EditComponentProps, TitleComponentProps, ViewComponentProps } from '..';
import { Currency } from '../../../types/currency';
import { currencyToCents } from '../Budget/formatCurrency';
import { AppI18n } from '../../../i18n';
import { HourlyRateEditComponent } from './HourlyRateEditComponent';
import { FixedPriceEditComponent } from './FixedPriceEditComponent';
import {
  BudgetAndTimelineContextProvider,
  BudgetAndTimelineContent,
  useBudgetAndTimelineContext,
  isValidContentStructure,
  useNeedType,
  getCommaFormatedStringAsNumber,
} from './utils';
import {
  BudgetChip,
  DeliveriesFrequencyChip,
  DeliveryTimelineChip,
  HiringDurationChip,
  TimeCommitmentChip,
} from './viewChips';

export const getBudgetInCents = (value: string, currency: Currency) => {
  const valueAsNumber = getCommaFormatedStringAsNumber(value);

  return currencyToCents(valueAsNumber, currency.rate);
};

const copyPrefix = 'sections.budget_and_timeline.' as const;
const titleCopy = `${copyPrefix}title` as const;
const editingTitleCopy = `${copyPrefix}title_editing` as const;

export const BudgetAndTimelineTitleComponent = ({
  isEditing,
  section,
  color,
}: TitleComponentProps<BudgetAndTimelineContent>) => {
  const content = (section?.content || {}) as BudgetAndTimelineContent;
  const chipsAreShown = isValidContentStructure(content) && !isEditing;

  if (chipsAreShown) {
    return null;
  }

  return (
    <Typography
      fontWeight="bold"
      className={atoms({
        whiteSpace: 'preWrap',
      })}
      marginBottom={isEditing ? '3' : '0'}
      {...(color && { color })}
    >
      <AppI18n k={isEditing ? editingTitleCopy : titleCopy} />
    </Typography>
  );
};

export const BudgetAndTimelineEditComponentWrapper = (props: EditComponentProps<BudgetAndTimelineContent>) => {
  const { section, onChange, onEditEnd, onCancel } = props;

  return (
    <BudgetAndTimelineContextProvider
      onChange={onChange}
      onEditEnd={onEditEnd}
      onCancel={onCancel}
      content={section.content || ({} as BudgetAndTimelineContent)}
    >
      <BudgetAndTimelineEditComponent {...props} />
    </BudgetAndTimelineContextProvider>
  );
};

export const BudgetAndTimelineViewComponent = ({
  section,
  forceUpdateContent,
}: ViewComponentProps<BudgetAndTimelineContent>) => {
  const content = (section.content || {}) as BudgetAndTimelineContent;
  const { timeline, timelineDetails = {} } = content;
  const { timeCommitment, hiringDuration, deliveriesFrequency } = timelineDetails;
  const { needType } = useNeedType();
  const shouldResetSectionContent = useRef(false);

  useEffect(() => {
    if (needType) {
      if (shouldResetSectionContent.current) {
        forceUpdateContent?.({
          pricingType: null,
          budgetInCents: 0,
          hourlyBudgetRange: { min: 0, max: 0 },
          timeline: undefined,
          deliveryDate: undefined,
          timelineDetails: {
            generalTimeline: {},
            deliveriesFrequency: {},
          },
        });
      } else {
        shouldResetSectionContent.current = true;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needType]);

  return (
    <Stack wrap="wrap" gap="10">
      <BudgetChip finalContent={content} />
      {!isEmpty(timeline) && timeline !== Gql.DmBriefTimeline.Ongoing && (
        <DeliveryTimelineChip finalContent={content} />
      )}
      {!isEmpty(timeCommitment) && <TimeCommitmentChip finalContent={content} />}
      {!isEmpty(hiringDuration) && <HiringDurationChip finalContent={content} />}
      {!isEmpty(deliveriesFrequency) && <DeliveriesFrequencyChip finalContent={content} />}
    </Stack>
  );
};

export const BudgetAndTimelineEditComponent: React.FC<EditComponentProps<BudgetAndTimelineContent>> = () => {
  const { sectionContentState, needType, isHourlyEligible, handlePricingTypeChange } = useBudgetAndTimelineContext();
  const isFixPriceMode: boolean = sectionContentState.pricingType !== Gql.BriefPricingType.Hourly;
  const isOnGoingMode = needType === DmBriefNeedType.Ongoing;
  const canChooseHourly = isOnGoingMode || isHourlyEligible;

  const pricingTypeOptions = isOnGoingMode
    ? Object.keys(Gql.BriefPricingType).reverse()
    : Object.keys(Gql.BriefPricingType);

  const getInstructionsCopy = (): 'hourly' | 'fixed_price_per_delivery' | 'fixed_price' => {
    if (!isFixPriceMode) {
      return 'hourly';
    } else if (isOnGoingMode) {
      return 'fixed_price_per_delivery';
    }
    return 'fixed_price';
  };

  return (
    <Stack direction="column" gap={canChooseHourly ? '8' : '6'}>
      <Stack direction="column" gap="4">
        {canChooseHourly && (
          <Stack>
            <ChoiceGroup
              value={sectionContentState.pricingType}
              onChange={handlePricingTypeChange}
              width="100%"
              direction={{ default: 'column', sm: 'row' }}
            >
              {pricingTypeOptions.map((key) => (
                <ChoiceGroupItem key={key} value={Gql.BriefPricingType[key]} flex="1">
                  <Wrap alignItems="flexStart" gap="4" padding="3" direction="column" justifyContent="flexStart">
                    {Gql.BriefPricingType[key] === Gql.BriefPricingType.Fixed ? (
                      <SinglePaymentL1Icon color="grey_1200" />
                    ) : (
                      <HourlyL1Icon color="grey_1200" />
                    )}
                    <Text fontWeight="semibold">
                      <AppI18n
                        k={
                          `sections.budget_and_timeline.${
                            Gql.BriefPricingType[key].toLowerCase() as 'hourly' | 'fixed'
                          }${
                            Gql.BriefPricingType[key] === Gql.BriefPricingType.Fixed && isOnGoingMode
                              ? '_per_delivery'
                              : ''
                          }` as any
                        }
                      />
                    </Text>
                  </Wrap>
                </ChoiceGroupItem>
              ))}
            </ChoiceGroup>
          </Stack>
        )}
        <Stack>
          <Text size="b_sm">
            <AppI18n k={`sections.budget_and_timeline.${getInstructionsCopy()}_instructions`} />
          </Text>
        </Stack>
      </Stack>
      <Stack>
        {isFixPriceMode ? <FixedPriceEditComponent canChooseHourly={canChooseHourly} /> : <HourlyRateEditComponent />}
      </Stack>
    </Stack>
  );
};
